import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";
import * as URL from "../../helpers/url_helper";

const api = new APIClient();

// ── Thunks ─────────────────────────────────────────────────────────
export const fetchLeads = createAsyncThunk("leads/fetchLeads",
  async ({ page = 0, size = 100 } = {}) =>
    api.get(URL.LW_GET_LEADS, { page, size })
);

export const searchLeads = createAsyncThunk("leads/searchLeads",
  async (q) => api.get(URL.LW_SEARCH_LEADS, { q })
);

export const getLead = createAsyncThunk("leads/getLead",
  async (id) => api.get(`${URL.LW_GET_LEADS}/${id}`)
);

export const createLead = createAsyncThunk("leads/createLead",
  async (data) => api.create(URL.LW_ADD_LEAD, data)
);

export const updateLead = createAsyncThunk("leads/updateLead",
  async ({ id, data }) => api.put(`${URL.LW_UPDATE_LEAD}/${id}`, data)
);

export const deleteLead = createAsyncThunk("leads/deleteLead",
  async (id) => { await api.delete(`${URL.LW_DELETE_LEAD}/${id}`); return id; }
);

// Bulk-delete all leads tagged source='SAMPLE' AND any customers that were
// auto-created from those leads (each lead carries `convertedCustomerId`
// after a WON drag). Real leads/customers are left untouched because we
// only ever touch IDs we explicitly captured from SAMPLE-tagged leads.
// Returns the lead IDs we removed so the reducer can prune state without
// a refetch; the customer slice is told to refetch separately by the page.
export const clearSampleLeads = createAsyncThunk("leads/clearSampleLeads",
  async (_, { getState }) => {
    const sampleLeads = (getState().Leads?.items || [])
      .filter((l) => l.source === 'SAMPLE');
    const leadIds = sampleLeads.map((l) => l.id);
    const customerIds = sampleLeads
      .map((l) => l.convertedCustomerId)
      .filter((id) => id != null);

    // Delete customers first so a partially-failed lead delete doesn't
    // leave behind an orphan customer pointing at a vanished lead.
    if (customerIds.length) {
      await Promise.allSettled(customerIds.map((id) => api.delete(`/customers/${id}`)));
    }
    await Promise.allSettled(leadIds.map((id) => api.delete(`${URL.LW_DELETE_LEAD}/${id}`)));
    return { leadIds, customerIds };
  }
);

export const changeLeadStage = createAsyncThunk("leads/changeLeadStage",
  async ({ id, stageId }) => api.update(`${URL.LW_CHANGE_LEAD_STAGE}/${id}/stage`, { stageId })
);

export const markLeadWon = createAsyncThunk("leads/markLeadWon",
  async ({ id, amount }) => api.create(`${URL.LW_MARK_LEAD_WON}/${id}/won`, { amount })
);

export const markLeadLost = createAsyncThunk("leads/markLeadLost",
  async ({ id, reason }) => api.create(`${URL.LW_MARK_LEAD_LOST}/${id}/lost`, { reason })
);

export const fetchLeadActivities = createAsyncThunk("leads/fetchLeadActivities",
  async (leadId) => {
    const data = await api.get(`${URL.LW_LEAD_ACTIVITIES}/${leadId}/activities`);
    return { leadId, data };
  }
);

export const addLeadActivity = createAsyncThunk("leads/addLeadActivity",
  async ({ leadId, type, payload }) =>
    api.create(`${URL.LW_LEAD_ACTIVITIES}/${leadId}/activities`, { type, payload })
);

// ── Slice ──────────────────────────────────────────────────────────
const initialState = {
  items: [],
  totalElements: 0,
  totalPages: 0,
  loading: false,
  saving: false,
  error: null,
  current: null,
  activitiesByLead: {},
  searchResults: []
};

const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    clearError(state) { state.error = null; },
    clearCurrent(state) { state.current = null; },
    // Optimistically move a lead to a new stage so drag-and-drop feels instant.
    // The server response from changeLeadStage will reconcile via replaceItem.
    optimisticStageMove(state, action) {
      const { id, stageId } = action.payload || {};
      const idx = state.items.findIndex((l) => l.id === id);
      if (idx >= 0) state.items[idx] = { ...state.items[idx], stageId };
      if (state.current && state.current.id === id) {
        state.current = { ...state.current, stageId };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLeads.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload?.content || [];
        s.totalElements = a.payload?.totalElements || 0;
        s.totalPages = a.payload?.totalPages || 0;
      })
      .addCase(fetchLeads.rejected, (s, a) => { s.loading = false; s.error = a.error.message; })

      .addCase(searchLeads.fulfilled, (s, a) => { s.searchResults = a.payload || []; })

      .addCase(getLead.fulfilled, (s, a) => { s.current = a.payload; })

      .addCase(createLead.pending, (s) => { s.saving = true; })
      .addCase(createLead.fulfilled, (s, a) => {
        s.saving = false;
        if (a.payload) s.items.unshift(a.payload);
      })
      .addCase(createLead.rejected, (s, a) => { s.saving = false; s.error = a.error.message; })

      .addCase(updateLead.fulfilled, replaceItem)
      .addCase(changeLeadStage.fulfilled, replaceItem)
      .addCase(markLeadWon.fulfilled, replaceItem)
      .addCase(markLeadLost.fulfilled, replaceItem)

      .addCase(deleteLead.fulfilled, (s, a) => {
        s.items = s.items.filter((l) => l.id !== a.payload);
      })

      .addCase(clearSampleLeads.fulfilled, (s, a) => {
        const removed = new Set(a.payload?.leadIds || []);
        s.items = s.items.filter((l) => !removed.has(l.id));
      })

      .addCase(fetchLeadActivities.fulfilled, (s, a) => {
        s.activitiesByLead[a.payload.leadId] = a.payload.data || [];
      })
      .addCase(addLeadActivity.fulfilled, (s, a) => {
        const act = a.payload;
        if (!act) return;
        const list = s.activitiesByLead[act.leadId] || [];
        s.activitiesByLead[act.leadId] = [act, ...list];
      });
  }
});

function replaceItem(state, action) {
  const updated = action.payload;
  if (!updated) return;
  state.items = state.items.map((l) => (l.id === updated.id ? updated : l));
  if (state.current && state.current.id === updated.id) state.current = updated;
}

export const { clearError, clearCurrent, optimisticStageMove } = leadsSlice.actions;
export default leadsSlice.reducer;
